import { Component, Fragment, useState, useRef, useEffect, useMemo } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Router, { useRouter } from 'next/router';
import Layout from "@/components/event-layout";
import LoggedIn from "@/components/logged-in";
import { Footer } from "@/pages/index";
import * as fbq from "@/lib/fbq";
import * as gtag from "@/lib/gtag";
import { trackEvent } from "@/lib/intixAnalytics";
import { DateTime } from 'luxon';
import axios from 'axios';
import cookie from 'js-cookie';
import styles from './index.module.css';
import GeneralAdmission, { GeneralAdmissionNew, NewLookPage } from './general_admission';
import gaStyles from './general_admission.module.css';
import ReservedSeating from './reserved_seating';
import { getContrast, RichResultsOutput, addAffiliateCookie, getPublicURL } from "@/lib/site";
import { CalendarToday, LocationCity, KeyboardArrowUp, KeyboardArrowDown, Close } from '@mui/icons-material';
import { Loading } from "@/lib/loading";
import HelpIcon from '@mui/icons-material/Help';

// translation content
import enAU from "@/locales/en-AU/common.json";
import enUS from "@/locales/en-US/common.json";

export async function getStaticPaths(){
	const event_json = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/events?limit=100`);
	const events = await event_json.json();
	const paths = events.map((event) => ({ params: { event_id: event.url_string } }));
	return { paths, fallback: 'blocking' };
}

export async function getStaticProps({ params }){
    const event = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/events/${params.event_id}`);
	if (event.status === 400 || event.status === 404) { return { notFound: true, revalidate: 60 }; }
	if (!event.ok) { throw new Error(`Event lookup failed for ${params.event_id} with status ${event.status}`); }
	const event_detail = await event.json();

	const [org_res, stripe_res, venue_res, faq_res, add_on_res, questions_res, tracking_pixel_res, features_res] = await Promise.all([
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/org/${event_detail.org_id}`),
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/org/${event_detail.org_id}/stripe`),
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/venue/${event_detail.venue_url_string}`),
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/events/${event_detail.event_id}/faq`),
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/events/${event_detail.event_id}/add_on`),
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/events/${event_detail.event_id}/questions`),
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/events/${event_detail.event_id}/tracking_pixels`),
		fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/org/${event_detail.org_id}/features`).catch(error => null),
	]);
	const [org_detail, stripe_detail, venue_detail, faq_list, add_ons_list, questions_list, tracking_pixel_list, features_detail] = await Promise.all([ org_res.json(), stripe_res.json(), venue_res.json(), faq_res.json(), add_on_res.json(), questions_res.json(), tracking_pixel_res.json(), features_res && features_res.ok ? features_res.json().catch(error => ({})) : {}, ]);

	let descriptionText = "Buy " + event_detail.title + " tickets";
	try {
        const descRes = await fetch('https://d2jun3ty2bmcrf.cloudfront.net/event/' + event_detail.event_id + '/summary.txt');
        if (descRes.ok){ descriptionText = await descRes.text(); }
    } catch (e){ /* Fallback already set */ }

	let longDescriptionText = "";
	try {
		const longDescRes = await fetch('https://d2jun3ty2bmcrf.cloudfront.net/event/' + event_detail.event_id + '/long_description.txt');
		if (longDescRes.ok){ 
			longDescriptionText = await longDescRes.text(); 
		}
	} catch (e){ /* Fallback already set */ }

	let expandedFaqs = [];
	if (Array.isArray(faq_list) && faq_list.length > 0) {
		expandedFaqs = await Promise.all(faq_list.map(async (faq) => {
			try {
				const textRes = await fetch(`https://d2jun3ty2bmcrf.cloudfront.net/event/${event_detail.event_id}/faq/${faq.short_code}.txt`);
				const htmlContent = await textRes.text();
				return { ...faq, html_content: htmlContent };
			} catch (e) {
				return { ...faq, html_content: "" };
			}
		}));
	}

	const server_time = Math.round(new Date().getTime() / 1000);
	const event_is_finished = server_time > event.utc_event_finish_date ||  event.event_status_id === 2 || event.event_status_id === 4;

	return { props : { event: event_detail, org: org_detail, stripe_account: stripe_detail, venue: venue_detail, add_ons: add_ons_list, questions: questions_list, faqs: expandedFaqs, tracking_pixels: Array.isArray(tracking_pixel_list) ? tracking_pixel_list : [], organizer_url: getPublicURL({ country_id: org_detail.country }) + "/org/" + org_detail.url_string, meta_description: descriptionText, long_description: longDescriptionText, server_time: server_time, org_beta: Array.isArray(features_detail.features) ? features_detail.features : [] }, revalidate: 600, notFound: false }
}

export default function Event({ event, org, stripe_account, venue, add_ons, questions, faqs, tracking_pixels = [], organizer_url, meta_description, long_description, server_time, org_beta = [] }){
	const [pageUrl, setPageUrl] = useState('');
	const [isContactModalOpen, setIsContactModalOpen] = useState(false);
	const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
	const [priceCategoryList, setPriceCategoryList] = useState([]);
	const [urlUnlockPromotion, setUrlUnlockPromotion] = useState(undefined);
	const [privateEventPasswordCorrect, setPrivateEventPasswordCorrect] = useState(false);
	const [eventPassword, setEventPassword] = useState('');
	const [queueCheckLoading, setQueueCheckLoading] = useState(true);
	const [activeQueueExists, setActiveQueueExists] = useState(false);
	const [queueSize, setQueueSize] = useState(0);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [loadingUser, setLoadingUser] = useState(false);
    const [user, setUser] = useState(undefined);
    const [showSignInModal, setShowSignInModal] = useState(false);

	function loadUser(){
		setLoadingUser(true);
		fetch(process.env.NEXT_PUBLIC_API_URL + '/protected/user', { method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: cookie.get("token") } })
		.then(response => response.json())
		.then(data => { if( data.error ){ setUser(null) } else { setUser(data);} })
		.catch(error => { setUser(null); })
		.finally(() => { setLoadingUser(false); });
	}

    useEffect(() => loadUser(), []) 

	const router = useRouter();
	const { locale } = router;
	const t = locale == "en-AU" ? enAU : locale == "en-US" ? enUS : enAU;

	if(router.isFallback || !event){ return ( <div className={styles.pOynwz}><Image src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/login-loading.svg" alt="Loading" title="Loading" width={100} height={100} priority /></div> )}

    // check for active queue and user's queue status
	useEffect(() => {
		const now = Math.round((new Date()).getTime() / 1000);
		const event_open_time = Math.floor(new Date(event.utc_booking_start_date).getTime() / 1000);
		const saleHasStarted = now >= event_open_time;

		if (saleHasStarted && event.waiting_room === 1) {
			let existingToken = null;
			try {
				const stored = JSON.parse(localStorage.getItem('queue_token'));
				if (stored?.event_id === event.event_id && stored.expiry > Date.now()) {
					existingToken = stored.token;
				} else if (stored) {
					localStorage.removeItem('queue_token'); 
				}
			} catch (e) {
				localStorage.removeItem('queue_token'); 
			}

			if (existingToken) {
				fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/queue/status/${event.event_id}?queue_token=${existingToken}`)
					.then(res => res.json())
					.then(data => {
						if (data.status === 'CHECKOUT' && data.can_access_event === true) {
							setActiveQueueExists(false);
							setQueueCheckLoading(false);
						} else if (data.status === 'QUEUED' || data.status === 'PRE_QUEUE') {
							setActiveQueueExists(true);
							setQueueCheckLoading(false);
						} else {
							checkQueueSize();
						}
					})
					.catch(() => checkQueueSize());
			} else {
				checkQueueSize();
			}
		} else {
			setQueueCheckLoading(false);
		}

		function checkQueueSize() {
			fetch(`${process.env.NEXT_PUBLIC_API_URL}/public/queue/size/${event.event_id}`)
				.then(res => res.json())
				.then(data => {
					if (data.queue_size > 0) {
						setActiveQueueExists(true);
						setQueueSize(data.queue_size);
					}
					setQueueCheckLoading(false);
				})
				.catch(() => setQueueCheckLoading(false));
		}
	}, [event.event_id, event.utc_booking_start_date, event.waiting_room]);

	useEffect(() => { setPageUrl("https://" + window.location.hostname + window.location.pathname); }, []);
	useEffect(() => { if (router.isReady ){ if( router.query.price_category_id != undefined ){ setPriceCategoryList(router.query.price_category_id.split(',').map( Number )); } } if( router.query.promotion_code != undefined ){ setUrlUnlockPromotion( router.query.promotion_code ) }; if( router.query.affiliate_code != undefined ){ addAffiliateCookie( event.event_id, router.query.affiliate_code ); } }, [router.isReady]);
	useEffect(() => { 
		if(tracking_pixels.some(e => e.type === 'facebook')){ fbq.pageview(); } 
		if(tracking_pixels.some(e => e.type === 'google_tag') || tracking_pixels.some(e => e.type === 'google_analytics')){ gtag.pageview(); } 
	}, []);

	function submitEventPassword(){
		fetch(process.env.NEXT_PUBLIC_API_URL + '/public/events/' + event.event_id + '/unlock', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ private_event_password: eventPassword }) })
		.then(response => response.json())
		.then(data => { console.log(data); if( data.password_correct == true ){ setPrivateEventPasswordCorrect(true); } else { console.log(data.error.message); setErrorMessage(data.error.message); } })
		.catch(error => { });
	}

	const new_look = event.reserved_seating !== 1 && org_beta.includes('ftr_3Lb6Bc08FCRNVXElrXRotKWkyW9'); // feature: ftr_3Lb6Bc08FCRNVXElrXRotKWkyW9 · I20-1008 · New look ticket page for general admission events

	if( event.event_status_id === 3 ){
		if( new_look ){
			return (
				<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
					<NewLookPage event={event} org={org} venue={venue} tracking_pixels={tracking_pixels} status="Postponed">
						<div className={gaStyles.gateContent}>
							<section className={gaStyles.card}>
								<p className={gaStyles.eyebrow}>Update from the organiser</p>
								<h2 className={gaStyles.groupTitle}>This event has been postponed</h2>
								<p className={gaStyles.gateText}>Please contact {org.name} directly for further information.</p>
								<div className={gaStyles.gateActions}><a href={"/org/" + org.url_string} className={gaStyles.ghostButton}>More events from {org.name}</a></div>
							</section>
						</div>
					</NewLookPage>
				</Layout>
			)
		}
		return (
			<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
				<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} />
				<div className={styles.dXMaQG}>
					<h2>This event has been postponed. Please contact {org.name} directly for further information.</h2>
					<p><a href="/discover">Search</a> for your next event experience, or check out other great <a href={`/org/${org.url_string}`}>{org.name} events</a>.</p>
				</div>
				<Footer />
			</Layout>
		)
	}

	if( server_time > event.utc_event_finish_date || event.event_status_id === 2 || event.event_status_id === 4 ){
		if( new_look ){
			return (
				<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
					<NewLookPage event={event} org={org} venue={venue} tracking_pixels={tracking_pixels} status="Event ended">
						<div className={gaStyles.gateContent}>
							<section className={gaStyles.card}>
								<p className={gaStyles.eyebrow}>This event</p>
								<h2 className={gaStyles.groupTitle}>This event has finished</h2>
								<p className={gaStyles.gateText}>It is either over or no longer on sale. See what {org.name} has coming up next.</p>
								<div className={gaStyles.gateActions}><a href={"/org/" + org.url_string} className={gaStyles.ctaLink}>Upcoming events</a><a href="/discover" className={gaStyles.ghostButton}>Search all events</a></div>
							</section>
						</div>
					</NewLookPage>
				</Layout>
			)
		}
		return (
			<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
				<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} />
				<div className={styles.dXMaQG}>
					<h2 dangerouslySetInnerHTML={{__html:t.event_finished_message}}></h2>
					<p><a href="/discover">Search</a> for your next event experience, or check out other great <a href={`/org/${org.url_string}`}>{org.name} events</a>.</p>
				</div>
				<Footer />
			</Layout>
		)
	}

	if( event.enable_presale > 0 ){
		if( new_look ){
			return (
				<Layout title={"Subscribe - " + event.title} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
					<NewLookPage event={event} org={org} venue={venue} tracking_pixels={tracking_pixels} status="Pre-sale sign-up">
						<div className={gaStyles.gateContent}><PresaleRegistration enable_presale={event.enable_presale} event_id={event.event_id} event_title={event.title} venue_title={venue.title} ga_event_start_date={event.ga_event_start_date} ga_event_start_time={event.ga_event_start_time} /></div>
					</NewLookPage>
				</Layout>
			)
		}
		return (
			<Layout title={"Subscribe - " + event.title} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
				<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} />
				<div id="fb-root"></div>
				<section className={styles.mainBanner}><div className={styles.mainBannerImage} style={{backgroundImage:"url('https://d2jun3ty2bmcrf.cloudfront.net/event/" + event.event_id + "/" + event.banner_image + "')",backgroundColor:"#" + org.background_color}}></div></section>
				<section className={styles.presaleContent}><PresaleRegistration enable_presale={event.enable_presale} event_id={event.event_id} event_title={event.title} venue_title={venue.title} ga_event_start_date={event.ga_event_start_date} ga_event_start_time={event.ga_event_start_time} /></section>
			</Layout>
		)
	}

	if( event.private_event_setting === 2 && privateEventPasswordCorrect === false ){
		if( new_look ){
			return (
				<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
					<NewLookPage event={event} org={org} venue={venue} tracking_pixels={tracking_pixels} status="Private event">
						<div className={gaStyles.gateContent}>
							<section className={gaStyles.card}>
								<p className={gaStyles.eyebrow}>Private event</p>
								<h2 className={gaStyles.groupTitle}>Enter the event password</h2>
								<form className={gaStyles.gateForm} onSubmit={e => { e.preventDefault(); submitEventPassword(); }}>
									<div className={gaStyles.gateField}><label className={gaStyles.fieldLabel} htmlFor="event_password">Event password</label><input id="event_password" className={gaStyles.codeInput} type="text" name="eventPassword" autoComplete="off" value={eventPassword} onChange={e => setEventPassword(e.target.value)} required={true} /></div>
									{errorMessage ? ( <p className={gaStyles.codeError} role="alert">{errorMessage}</p> ) : null}
									<button type="submit" className={gaStyles.cta} disabled={eventPassword.length < 3}>Unlock</button>
								</form>
							</section>
						</div>
					</NewLookPage>
				</Layout>
			)
		}
		return (
			<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
				<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} />
				<div className={styles.ggsJNx}>
					<div className={styles.arTvz5}>
						<div className={styles.ebPhLx}>
							<h2 className={styles.iYQXGs}>UNLOCK THIS EVENT</h2>
							<div className={styles.mKLoVf}><span className={styles.xBnUtQ} style={{borderColor: "#" + org.background_color}}></span></div>
							<div className={styles.cVPeND}>
								<div className={styles.tYNzsb}>
									<input autoComplete={"off"} className={styles.rTTbza} value={eventPassword} onChange={e => setEventPassword(e.target.value)} type="text" name="eventPassowrd" placeholder="Enter the event password" required={true} />
								</div>
                                {errorMessage && <span className={styles.errorMessage}>{errorMessage}</span>}
								<button className={styles.wREcvg} disabled={eventPassword.length < 3 } onClick={e => submitEventPassword()}>Submit</button>
							</div>
						</div>
					</div>
				</div>
			</Layout>
		)
	}

	if( server_time < event.utc_booking_start_date ){
		const timeUntilSale = event.utc_booking_start_date - Math.round((new Date()).getTime() / 1000);
        // hard code window to 30 mins for testing
		const waitingRoomWindow = 30 * 60; 

		if( event.waiting_room === 1 && timeUntilSale <= waitingRoomWindow ){
            // if ( 1 == true) {
			return (
				<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
					<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} transparent={true} />
					<WaitingRoom event={event} org={org} utc_booking_start_date={event.utc_booking_start_date} user={user} loadingUser={loadingUser} />
				</Layout>
			)
		}

		if( new_look ){
			return (
				<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
					<NewLookPage event={event} org={org} venue={venue} tracking_pixels={tracking_pixels}>
						<div className={gaStyles.gateContent}>
							<section className={gaStyles.card}>
								<p className={gaStyles.eyebrow}>{event.event_type === 1 ? 'Registrations open' : 'Tickets go on sale'}</p>
								<h2 className={gaStyles.groupTitle}>{event.formatted_countdown_booking_start_date}</h2>
								<OpeningCountdown utc_booking_start_date={event.utc_booking_start_date} />
								<p className={gaStyles.gateText}>Keep this page open. You will be taken to the {event.event_type === 1 ? 'registration' : 'tickets'} page when the sale starts.</p>
								<CheckLoginCheck event_id={event.event_id} org_name={org.name} />
							</section>
						</div>
					</NewLookPage>
					<RichResultsOutput schema={RichResultsSchema({"event_title":event.title, "url_string":event.url_string, "currency":event.currency, "min_price":event.min_ticket_value, "max_price":event.max_ticket_value, "online_event":event.online_event, "booking_start_date_time_formatted":event.rich_result_booking_start_date, "start_date_time_formatted":event.rich_result_event_start_date, "finish_date_time_formatted":event.rich_result_event_finish_date, "header":"https://d2jun3ty2bmcrf.cloudfront.net/event/" + event.event_id + "/" + event.banner_image, "summary":meta_description, "organiser":org.name, "location":venue.title, "address1":venue.address1, "suburb":venue.suburb, "postcode":venue.postcode, "state":venue.state, "country":venue.country, "latitude":venue.latitude, "longitude": venue.longitude, "organizer_name": org.name, "organizer_url": organizer_url })} />
				</Layout>
			)
		}

		return (
			<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
				<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} />
				<div className={styles.arTvz5}>
					<div className={styles.ebPhLx}>
						<h2 className={styles.iYQXGs}>ONSALE TIMES</h2>
						<div className={styles.mKLoVf}><span className={styles.xBnUtQ} style={{borderColor: "#" + org.background_color}}></span></div>
						<div className={styles.cVPeND}>
							<div className={styles.hbIcQB}>
								<div className={styles.gvOMnV}>
									<span className={styles.exRmQk}>From:</span>
									<span className={styles.bwIobK}>{event.formatted_countdown_booking_start_date}</span>
								</div>
							</div>
							<OpeningCountdown utc_booking_start_date={event.utc_booking_start_date} />
						</div>
						<CheckLoginCheck event_id={event.event_id} org_name={org.name} />
						<p className={styles.yyTBxq}>Do NOT refresh this page. You will automatically be taken to the buying process.</p>
					</div>
				</div>
				<RichResultsOutput schema={RichResultsSchema({"event_title":event.title, "url_string":event.url_string, "currency":event.currency, "min_price":event.min_ticket_value, "max_price":event.max_ticket_value, "online_event":event.online_event, "booking_start_date_time_formatted":event.rich_result_booking_start_date, "start_date_time_formatted":event.rich_result_event_start_date, "finish_date_time_formatted":event.rich_result_event_finish_date, "header":"https://d2jun3ty2bmcrf.cloudfront.net/event/" + event.event_id + "/" + event.banner_image, "summary":meta_description, "organiser":org.name, "location":venue.title, "address1":venue.address1, "suburb":venue.suburb, "postcode":venue.postcode, "state":venue.state, "country":venue.country, "latitude":venue.latitude, "longitude": venue.longitude, "organizer_name": org.name, "organizer_url": organizer_url })} />
			</Layout>
		)
	}

	if (queueCheckLoading) {
		return (
			<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
				<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} />
				<div className={styles.pOynwz}><Image src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/login-loading.svg" alt="Loading" title="Loading" width={100} height={100} priority /></div>
			</Layout>
		)
	}

	if (activeQueueExists) {
		return (
			<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
				<EventHeader org={org} event={event} tracking_pixels={tracking_pixels} transparent={true} />
				<WaitingRoom event={event} org={org} utc_booking_start_date={event.utc_booking_start_date} initialQueueSize={queueSize} user={user} loadingUser={loadingUser} />
				<RichResultsOutput schema={RichResultsSchema({"event_title":event.title, "url_string":event.url_string, "currency":event.currency, "min_price":event.min_ticket_value, "max_price":event.max_ticket_value, "online_event":event.online_event, "booking_start_date_time_formatted":event.rich_result_booking_start_date, "start_date_time_formatted":event.rich_result_event_start_date, "finish_date_time_formatted":event.rich_result_event_finish_date, "header":"https://d2jun3ty2bmcrf.cloudfront.net/event/" + event.event_id + "/" + event.banner_image, "summary":meta_description, "organiser":org.name, "location":venue.title, "address1":venue.address1, "suburb":venue.suburb, "postcode":venue.postcode, "state":venue.state, "country":venue.country, "latitude":venue.latitude, "longitude": venue.longitude, "organizer_name": org.name, "organizer_url": organizer_url })} />
			</Layout>
		)
	}

	return (
		<Layout title={event.event_type === 0 ? ("Buy " + event.title + " tickets") : ("Register for " + event.title)} logo={org.logo_image} theme={org.background_color} org_url_string={org.url_string} org_title={org.name} event_id={event.event_id} org_id={event.org_id} event_image={event.banner_image} meta_description={meta_description} page_url={pageUrl} robots_meta={event.public_event === 1 && event.event_status_id === 1 ? 'all' : 'none' } favicon={org.favicon}>
			{ event.reserved_seating === 1 ? (
				<ReservedSeating event={event} org={org} stripe_account={stripe_account} venue={venue} tracking_pixels={tracking_pixels} long_description={long_description} faqs={faqs} />
			) : new_look ? (
				<GeneralAdmissionNew event={event} org={org} stripe_account={stripe_account} venue={venue} add_ons={add_ons} questions={questions} tracking_pixels={tracking_pixels} server_time={server_time} />
			) : (
				<GeneralAdmission event={event} org={org} stripe_account={stripe_account} venue={venue} add_ons={add_ons} questions={questions} tracking_pixels={tracking_pixels} server_time={server_time} />
			)}
			<RichResultsOutput schema={RichResultsSchema({"event_title":event.title, "url_string":event.url_string, "currency":event.currency, "min_price":event.min_ticket_value, "max_price":event.max_ticket_value, "online_event":event.online_event, "booking_start_date_time_formatted":event.rich_result_booking_start_date, "start_date_time_formatted":event.rich_result_event_start_date, "finish_date_time_formatted":event.rich_result_event_finish_date, "header":"https://d2jun3ty2bmcrf.cloudfront.net/event/" + event.event_id + "/" + event.banner_image, "summary":meta_description, "organiser":org.name, "location":venue.title, "address1":venue.address1, "suburb":venue.suburb, "postcode":venue.postcode, "state":venue.state, "country":venue.country, "latitude":venue.latitude, "longitude": venue.longitude, "organizer_name": org.name, "organizer_url": organizer_url })} />
		</Layout>
	)
}

export const EventHeader = ({ org, event, tracking_pixels, transparent = false }) => {
	return (
		<Fragment>
			<Head>
				<link rel="alternate" href={"https://www.intix.com/event/" + event.url_string} hrefLang="x-default" />
				<link rel="alternate" href={"https://www.intix.com/event/" + event.url_string} hrefLang="en" />
				<link rel="alternate" href={"https://www.intix.com/event/" + event.url_string} hrefLang="en-us" />
				<link rel="alternate" href={"https://www.intix.com.au/event/" + event.url_string} hrefLang="en-au" />
				<link rel="alternate" href={"https://www.intix.co.uk/event/" + event.url_string} hrefLang="en-gb" />
				<link rel="alternate" href={"https://www.intix.co.nz/event/" + event.url_string} hrefLang="en-nz" />
				<meta name="format-detection" content="telephone=no" />
				<meta name="theme-color" content={"#"+org.background_color} />
				{tracking_pixels.filter(e => e.type == 'facebook').map((item,index) => ( <script key={"facebook_pixel_" + index} id={"fb-pixel-" + index} dangerouslySetInnerHTML={{__html:`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', ${item.pixel});` }} /> ))}
				{tracking_pixels.filter(e => e.type == 'google_analytics').map((item,index) => ( <Fragment key={"ga4-" + index}><script async src={"https://www.googletagmanager.com/gtag/js?id=" + item.pixel}></script><script key={"google_analytics_" + index} id={"google-tag-" + index} dangerouslySetInnerHTML={{__html:`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${item.pixel}');` }} /></Fragment> ))}
				{tracking_pixels.filter(e => e.type == 'google_tag').map((item,index) => ( <Fragment key={"gtm-" + index}><script async src={"https://www.googletagmanager.com/gtag/js?id=" + item.pixel}></script><script key={"google_tag_" + index} id={"gtm-" + index} dangerouslySetInnerHTML={{__html:`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${item.pixel}');` }} /></Fragment> ))}
				{tracking_pixels.filter(e => e.type == 'tiktok').map((item,index) => ( <script key={"tiktok_" + index} id={"tiktok-tag-" + index} dangerouslySetInnerHTML={{__html:`!function (w, d, t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=i+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};ttq.load('${item.pixel}');ttq.page();}(window, document, 'ttq');` }} /> ))}
			</Head>
			<div id="orgHeader" className={(transparent ? styles.headerTransparent : styles.headerUnfixed) + " header"} style={transparent ? {} : {backgroundColor:'#' + org.background_color}}> 
				<nav className="navbar navbar-default" role="navigation">
					<div className="navbar-header">{ org.logo_image != null && org.logo_image.length > 0 ? ( <a href={"/org/" + org.url_string} className="navbar-brand" style={{marginLeft:"1.5rem"}}><Image src={"https://d2jun3ty2bmcrf.cloudfront.net/org/" + org.url_string + "/" + org.logo_image} alt={org.name} width={82} height={41} priority /></a> ) : ( <a href="/" className="navbar-brand"><Image src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/logo-small-console.png" alt="INTIX" title="INTIX" width={149} height={41} priority /></a> )}</div>
                    {/* we don't want to use Link here because it will not open in a new tab */}
                    {/* <div className="navbar-collapse" style={{height:"75px"}} id="example-navbar-collapse"><ul className="nav navbar-nav" style={{height:"75px"}}><li><a href="/account" target='_blank' rel='noopener noreferrer' style={{color:"#"+getContrast(org.background_color)}}>My Account</a></li></ul></div> */}
				</nav>
			</div>
		</Fragment>
	)
}

export function RichResultsSchema({ event_title, url_string, currency, booking_start_date_time_formatted, start_date_time_formatted, finish_date_time_formatted, header, summary, organiser, location, address1, suburb, postcode, state, country, latitude, longitude, performers, min_price, max_price, price_valid_until, online_event, organizer_name, organizer_url }){
	// Ticket values are stored in minor units (cents); omit offers entirely when no price is known rather than assert a wrong one.
	const offersSchema = ( min_price !== null && min_price !== undefined ) ? {"@type":"AggregateOffer","url":"https://www.intix.com/event/" + url_string,"priceCurrency":currency,"availability":"https://schema.org/InStock","lowPrice":( min_price / 100 ).toFixed(2),...( max_price !== null && max_price !== undefined ? {"highPrice":( max_price / 100 ).toFixed(2)} : {}),...( price_valid_until ? {"priceValidUntil":price_valid_until} : {}),"validFrom":booking_start_date_time_formatted} : undefined;
	const locationSchema = online_event ? {"@type":"VirtualLocation","url":"https://www.intix.com/event/" + url_string} : {"@type": "Place","name":location,...( latitude && longitude ? {"geo":{"@type":"GeoCoordinates","latitude":latitude,"longitude":longitude}} : {}),"address":{"@type": "PostalAddress","streetAddress":address1,"addressLocality":suburb,"postalCode":postcode,"addressRegion":state,"addressCountry":country}};
	return { structuredJSON: {"@context":"https://schema.org","@type":"Event","name":event_title,"eventStatus":"https://schema.org/EventScheduled",...(performers && performers.length ? {"performer":performers} : {}),...(offersSchema ? {"offers":offersSchema} : {}),...( min_price !== null && min_price !== undefined && Number(min_price) === 0 ? {"isAccessibleForFree":true} : {}),"eventAttendanceMode": online_event ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode","startDate":start_date_time_formatted,"endDate":finish_date_time_formatted,"image":[ header ],"description":summary,"organizer":{"@type":"Organization","name": organizer_name ,"url": organizer_url},"location":locationSchema}, id:"event_" + url_string }
}

export function BrandedLoading({ logo_image, background_color, url_string }){
	return ( <Fragment>{logo_image != null && logo_image.length > 0 ? ( <div className={styles.k2cmt6k66zh}><div className={styles.wocglevpr7}><div className={styles.d4gg5} style={{borderColor:'#' + background_color,borderTopColor:"transparent"}}></div><div className={styles.rT5nH} style={{backgroundColor:'#' + background_color}}><Image src={"https://d2jun3ty2bmcrf.cloudfront.net/org/" + url_string + "/" + logo_image} width={90} height={45} alt={url_string + " loading..."} /></div></div></div> ) : ( <Image src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/login-loading.svg" alt="Loading..." title="Loading..." className={styles.nM7Gf2} width={100} height={100} /> )}</Fragment> )
}

export class OpeningCountdown extends Component {
	constructor(props) {
		super(props);
		this.state = { loading: true, loading_event: false, days_remaining: 0, hours_remaining: 0, minutes_remaining: 0, seconds_remaining: 0 };
	}

	countdownData(){
		var end = DateTime.fromSeconds(this.props.utc_booking_start_date);
		var difference = end.diff(DateTime.utc(), ['seconds']);
		if( difference.values.seconds <= 0 ){
			this.setState({ loading_event: true });
			console.log('times up!');
			window.location.reload(true);
			clearInterval(this.myInterval);
		} else {
			let days = 0;
			let hours = 0;
			let minutes = 0;
			let seconds = 0;
			if( difference.values.seconds > 86400 ){ 
				days = Math.floor(difference.values.seconds / 86400) ;
				hours = Math.floor( ( difference.values.seconds - ( days * 86400 ) ) / 3600 );
				minutes = Math.floor( ( difference.values.seconds - ( days * 86400 ) - ( hours * 3600 ) ) / 60 );
				seconds = Math.floor( difference.values.seconds - ( days * 86400 ) - ( hours * 3600 ) - ( minutes * 60 ) );
			}
			if( difference.values.seconds < 86399 && difference.values.seconds > 3600 ){ 
				hours = Math.floor(difference.values.seconds / 3600);
				minutes = Math.floor( ( difference.values.seconds - ( hours * 3600 ) ) / 60 );
				seconds = Math.floor( difference.values.seconds - ( hours * 3600 ) - ( minutes * 60 ) );
			}
			if( difference.values.seconds < 3599 && difference.values.seconds > 60 ){ 
				minutes = Math.floor( difference.values.seconds / 60 );
				seconds = Math.floor( difference.values.seconds - ( minutes * 60 ) );
			}
			if( difference.values.seconds < 59 ){ 
				seconds = Math.floor(difference.values.seconds);
			}

			this.setState({ days_remaining: days, hours_remaining: hours, minutes_remaining: minutes, seconds_remaining: seconds, loading: false });
		}
	}

	componentDidMount(){
		const thisCountdownData = this.countdownData.bind(this);
		this.myInterval = setInterval(thisCountdownData, 1000);
	}
	
	render(){
		return (
			<Fragment>
				{ this.state.loading_event === true ? (
					<div className={styles.hTRmfd}><img src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/login-loading.svg" alt="Loading" title="Loading" width="100" /></div>
				) : (
					<div className={styles.hmnRUM}>
						<div className={styles.ieKsdp}>
							<div className={styles.hMfYRO}>
								<div className={styles.fpYDtP}>{this.state.days_remaining}</div>
								<div className={styles.jScyrq}>Days</div>
							</div>
							<div className={styles.eWiWkJ}></div>
							<div className={styles.hMfYRO}>
								<div className={styles.fpYDtP}>{this.state.hours_remaining}</div>
								<div className={styles.jScyrq}>Hrs</div>
							</div>
							<div className={styles.eWiWkJ}></div>
							<div className={styles.hMfYRO}>
								<div className={styles.fpYDtP}>{this.state.minutes_remaining}</div>
								<div className={styles.jScyrq}>Mins</div>
							</div>
							<div className={styles.eWiWkJ}></div>
							<div className={styles.hMfYRO}>
								<div className={styles.fpYDtP}>{this.state.seconds_remaining}</div>
								<div className={styles.jScyrq}>Secs</div>
							</div>
						</div>
					</div>
				)}
			</Fragment>
		)
	}
}

export function CheckLoginCheck({ event_id, org_name }){
	const [loading, setLoading] = useState(true);
	const [loggedIn, setLoggedIn] = useState(false);

	useEffect(() => { checkLogin(); }, []);

	function checkLogin(){ axios.get(process.env.NEXT_PUBLIC_API_URL + '/public/me', { headers: { Authorization: cookie.get("token") } }).then(res => { if('user_id' in res.data){ setLoggedIn(true); setLoading(false); } else { setLoggedIn(false); setLoading(false); } }, error => { setLoggedIn(false); setLoading(false); }); }
	if( loading === false && loggedIn === false ){ return ( <p style={{textAlign:"center"}}><b>You are not currently logged in. {org_name} recommend logging in to complete purchase as quick as possible when sales open.</b></p> ) }

	return ( <div style={{display:"none",width:"1px",height:"1px;"}}></div> )
}

function PresaleRegistration({ enable_presale, event_id, event_title, venue_title, ga_event_start_date, ga_event_start_time }){
	const [loading, setLoading] = useState(false);
	const [emailAddress, setEmailAddress] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [mobileNumber, setMobileNumber] = useState('');
	const [postcode, setPostcode] = useState('');
	const [complete, setComplete] = useState(false);
	const [referralCode, setReferralCode] = useState(undefined);
	const [updatesConsent, setUpdatesConsent] = useState(false);

	const router = useRouter();
	useEffect(() => { if( router.query.referral_code != undefined ){ setReferralCode( router.query.referral_code ) }; }, [router.isReady]);
	
	function submitForm(){
		setLoading(true);
		fetch(process.env.NEXT_PUBLIC_API_URL + "/public/events/" + event_id + "/presale/registration", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email_address: emailAddress, first_name: firstName, last_name: lastName, mobile_number: mobileNumber, postcode: postcode, referral_code: referralCode }) })
		.then((response) => response.json())
		.then((data) => { if(data.error){ } else { setComplete(true); } })
		.catch(error => { console.log(error); })
		.finally(() => { setLoading(false); });
	}

	return (
		<div className={styles.yTTpoi}>
			<div className={styles.pUUixk}>
				<div className={styles.tagBNx}>
					<h1>SIGN UP FOR EARLY ACCESS</h1>
					<p className={styles.qRbnza}><i>{venue_title} | {ga_event_start_date} @ {ga_event_start_time}</i></p>
					{Number(event_id) === 10138 ? (
						<ul className={styles.mQvbrt}>
							<li><span>Wed 26th 2PM</span>Sign ups close</li>
							<li><span>Wed 26th 5PM</span>Pre-sale first access</li>
							<li><span>Thu 27th 10AM</span>General sale</li>
						</ul>
					) : null}
					{complete === true ? ( <p>We'll contact you soon!</p> ) : null}
					{complete === false ? (
						<div className={styles.ratnsm}>
							<div className={styles.tYNzsb}>
								<input className={styles.rTTbza} value={emailAddress} onChange={e => setEmailAddress(e.target.value.trim().toLowerCase())} type="email" name="emailAddress" placeholder="Your email address" required={true} />
							</div>
							<div className={styles.pYYbxz}>
								<div className={styles.tYNzsb}>
									<input className={styles.rTTbza} value={firstName} onChange={e => setFirstName(e.target.value)} type="text" name="firstName" placeholder="Your first name" required={true} />
								</div>
								<div className={styles.tYNzsb}>
									<input className={styles.rTTbza} value={lastName} onChange={e => setLastName(e.target.value)} type="text" name="lastName" placeholder="Your last name" required={true} />
								</div>
							</div>
							<div className={styles.pYYbxz}>
								<div className={styles.tYNzsb}>
									<input className={styles.rTTbza} value={mobileNumber} onChange={e => setMobileNumber(e.target.value.trim())} type="text" name="mobileNumber" placeholder="Your mobile number" required={true} />
								</div>
								<div className={styles.tYNzsb}>
									<input className={styles.rTTbza} value={postcode} onChange={e => setPostcode(e.target.value.trim())} type="text" name="postcode" placeholder="Your postcode" required={true} />
								</div>
							</div>
							{Number(event_id) === 10138 ? (
								<div className={styles.zBnqxt}>
									<label><input type="checkbox" checked={updatesConsent} onChange={e => setUpdatesConsent(e.target.checked)} /><span>I want to receive updates about Silva Bumpa and agree to the processing of my personal data in accordance with <a onClick={e => e.stopPropagation()} href="https://tools.sonymusiccreative.com/privacy/" rel="noreferrer" target="_blank">Sony Music Entertainment's privacy &amp; cookie policy</a></span></label>
								</div>
							) : null}
							<button className={styles.wREcvg} disabled={loading || emailAddress.length < 5 || firstName.length < 2 || lastName.length < 2 || mobileNumber.length < 7 || postcode.length < 3 || ( Number(event_id) === 10138 && updatesConsent === false )} onClick={e => submitForm()}>{loading === true ? ( <span className={styles.HZUPu}><img src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/button-loading.svg" width={25} /></span> ) : "Register"}</button>
						</div>
					) : null}
				</div>
			</div>
		</div>
	)
}

function WaitingRoom({ event, org, utc_booking_start_date, initialQueueSize = 0, user, loadingUser }){
	const [loading, setLoading] = useState(false);
	const [queuePosition, setQueuePosition] = useState(0);
	const [initialQueuePosition, setInitialQueuePosition] = useState(null);
	const [queueStatus, setQueueStatus] = useState('WAITING'); // WAITING, QUEUED, CHECKOUT
	const [secondsRemaining, setSecondsRemaining] = useState(0);
	const [error, setError] = useState(null);
	const [showTooltip, setShowTooltip] = useState(false);
	const [recaptchaVerified, setRecaptchaVerified] = useState(false);
	const [recaptchaToken, setRecaptchaToken] = useState(null);
	const [hasJoinedQueue, setHasJoinedQueue] = useState(false);
	const [statusCheckCountdown, setStatusCheckCountdown] = useState(null);
	const recaptchaRef = useRef(null);

	// Load reCAPTCHA script
	useEffect(() => {
		const existingScript = document.querySelector('script[src*="recaptcha"]');
		if (!existingScript) {
			const script = document.createElement('script');
			script.src = 'https://www.google.com/recaptcha/api.js';
			script.async = true;
			script.defer = true;
			document.head.appendChild(script);
		}

		// Define the callback function for reCAPTCHA
		window.onRecaptchaVerify = (token) => {
			setRecaptchaToken(token);
			setRecaptchaVerified(true);
		};

		window.onRecaptchaExpired = () => {
			setRecaptchaToken(null);
			setRecaptchaVerified(false);
		};

		return () => {
			delete window.onRecaptchaVerify;
			delete window.onRecaptchaExpired;
		};
	}, []);

	// Check if user already has a queue token (returning user)
	useEffect(() => {
		try {
			const stored = JSON.parse(localStorage.getItem('queue_token'));
			if (stored?.event_id === event.event_id && stored.expiry > Date.now()) {
				setHasJoinedQueue(true);
				checkQueueStatus();
			} else if (stored) {
				localStorage.removeItem('queue_token'); 
			}
		} catch (e) {
			localStorage.removeItem('queue_token');
		}
	}, []);

	// Explicitly render reCAPTCHA when user is loaded 
	useEffect(() => {
		if (!loadingUser && user && !hasJoinedQueue && window.grecaptcha?.render) {
			const container = document.querySelector('.g-recaptcha');
			if (container && !container.hasChildNodes()) {
				try {
					window.grecaptcha.render(container, {
						sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
						callback: window.onRecaptchaVerify,
						'expired-callback': window.onRecaptchaExpired
					});
				} catch (e) {
					// Already rendered
				}
			}
		}
	}, [loadingUser, user, hasJoinedQueue]);

	useEffect(() => {
		const countdown = setInterval(() => {
			const now = Math.round((new Date()).getTime() / 1000);
			const remaining = utc_booking_start_date - now;
			setSecondsRemaining(remaining);
		}, 1000);

		const statusPoll = setInterval(() => {
			if (hasJoinedQueue) {
				checkQueueStatus();
				setStatusCheckCountdown(5);
			}
		}, 5000);

		return () => {
			clearInterval(countdown);
			clearInterval(statusPoll);
		};
	}, [hasJoinedQueue]);

	useEffect(() => {
		if (statusCheckCountdown === null || statusCheckCountdown <= 0) return;

		const timer = setTimeout(() => {
			setStatusCheckCountdown(prev => prev - 1);
		}, 1000);

		return () => clearTimeout(timer);
	}, [statusCheckCountdown]);

	async function joinQueue(){
		if (!user) {
			setError('Please sign in before joining the queue.');
			return;
		}
		if (!recaptchaToken) {
			setError('Please complete the reCAPTCHA verification.');
			return;
		}

        const unix_now = Math.floor(Date.now() / 1000);
        const event_open_time = Math.floor(new Date(event.utc_booking_start_date).getTime() / 1000);

        console.log('joinQueue');
		try {
			setLoading(true);
			const response = await fetch(process.env.NEXT_PUBLIC_API_URL + '/protected/queue/join/' + event.event_id, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Authorization: cookie.get("token") },
				body: JSON.stringify({ recaptcha_token: recaptchaToken }),
			});

			const data = await response.json();

			if(data.error){
				setError(data.error.message);
				setLoading(false);
				if (window.grecaptcha) {
					window.grecaptcha.reset();
				}
				setRecaptchaVerified(false);
				setRecaptchaToken(null);
				return;
			}

			// store queue token in localStorage with 1hr TTL (persists across tabs)
			if(data.queue_token){
				const tokenData = {
					token: data.queue_token,
					event_id: event.event_id,
					expiry: Date.now() + (60 * 60 * 1000)
				};
				localStorage.setItem('queue_token', JSON.stringify(tokenData));
			}

			setHasJoinedQueue(true);
			setQueueStatus(data.status);
			setQueuePosition(data.queue_position);
			if(data.queue_position && !initialQueuePosition){
				setInitialQueuePosition(data.queue_position);
			}
            if (unix_now >= event_open_time) {
                checkQueueStatus();
            }
			setLoading(false);
            console.log('joinqueuedata', data);
		} catch(err){
			console.error('Join queue error:', err);
			setError('Unable to join waiting room. Please refresh the page.');
			setLoading(false);
			// Reset reCAPTCHA on error
			if (window.grecaptcha) {
				window.grecaptcha.reset();
			}
			setRecaptchaVerified(false);
			setRecaptchaToken(null);
		}
	}

	async function checkQueueStatus(){
		try {
			let queueToken = null;
			try {
				const stored = JSON.parse(localStorage.getItem('queue_token'));
				if (stored && stored.event_id === event.event_id && stored.expiry > Date.now()) {
					queueToken = stored.token;
				} else if (stored) {
					localStorage.removeItem('queue_token');
				}
			} catch (e) {
				localStorage.removeItem('queue_token');
			}
			if(!queueToken) return;

			const response = await fetch(
				process.env.NEXT_PUBLIC_API_URL + '/public/queue/status/' + event.event_id + '?queue_token=' + queueToken,
				{
					method: 'GET',
					headers: { 'Content-Type': 'application/json' }
				}
			);

			const data = await response.json();
            console.log('queuestatusdata', data);

			if(data.error){
				console.error('Queue status error:', data.error);
				return;
			}

			setQueueStatus(data.status);
			setQueuePosition(data.queue_position);
			if(data.queue_position && !initialQueuePosition){
				setInitialQueuePosition(data.queue_position);
			}

			// If granted access to checkout, redirect to event page
			if(data.can_access_event === true && data.status === 'CHECKOUT'){
				setTimeout(() => {
					window.location.reload(true);
				}, 1500);
			}
		} catch(err){
			console.error('Check status error:', err);
		}
	}

	function formatTime(seconds){
		if(seconds <= 0) return '00:00';
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
	}

	const steps = [
		{ id: 1, label: 'Waiting Room', status: 'WAITING' },
		{ id: 2, label: 'Queue', status: 'QUEUED' },
		{ id: 3, label: event.reserved_seating === 1 ? 'Pick Your Seats' : 'Pick Your Tickets', status: 'CHECKOUT' }
	];

	const getCurrentStep = () => {
		if (queueStatus === 'WAITING' || queueStatus === 'PRE_QUEUE') return 1;
		if (queueStatus === 'QUEUED') return 2;
		if (queueStatus === 'CHECKOUT') return 3;
		return 1;
	};

    const handleAuthRedirect = () => {
        const host = typeof window !== 'undefined' ? window.location.host : '';
        const redirectURL = process.env.NEXT_PUBLIC_USER_BRANCH === 'prod' ? `https://${host}/event/${event.url_string}` : `http://${host}/event/${event.url_string}`

        let region = 'default';
        if (host.includes('.com.au')) region = 'au';
        else if (host.includes('.co.nz')) region = 'nz';
        else if (host.includes('.co.uk')) region = 'uk';

        const authBase = process.env.NEXT_PUBLIC_USER_BRANCH === 'prod' ? 'https://auth.intix.com/signin' : 'http://localhost:3001/signin';
        return `${authBase}?redirectTo=${encodeURIComponent(redirectURL)}&region=${region}`;
    }

	const currentStep = getCurrentStep();

    return (
        <div className={styles.waitingRoomBackground} style={{ '--org-color':'#'+org.background_color }}> 
			<div className={styles.waitingRoomBanner} style={{backgroundImage:"url('https://d2jun3ty2bmcrf.cloudfront.net/event/" + event.event_id + "/" + event.banner_image + "')"}}></div>
            <div style={{borderTop: "10px solid #" + org.background_color}} className={styles.arTvz6}>
				<div className={styles.queueHelpButton} onClick={() => setShowTooltip(!showTooltip)}>
					<HelpIcon />
					{showTooltip && (
						<div className={styles.queueHelpTooltip}>
							<p><strong>How does the queue work?</strong></p>
							<p>When tickets go on sale, everyone in the waiting room is randomly assigned a position in the queue. You'll be automatically redirected when it's your turn to purchase.</p>
							<p>Stay on this page and don't refresh - your position is saved to this browser session.</p>
						</div>
					)}
				</div>
                <div className={styles.ebPhLx}>
                    <h2 className={styles.iYQXGs}>{!hasJoinedQueue ? 'JOIN THE WAITING ROOM' : queueStatus === 'QUEUED' ? 'YOU ARE NOW IN THE QUEUE' : queueStatus === 'CHECKOUT' ? "YOU'RE IN!" : 'YOU ARE NOW IN THE WAITING ROOM'}</h2>
                    <div className={styles.mKLoVf}><span className={styles.xBnUtQ} style={{borderColor: "#" + org.background_color}}></span></div>

				{/* Progress Stepper */}
				<div className={styles.stepperContainer}>
					{steps.map((step, index) => (
						<div key={step.id} className={styles.stepWrapper}>
							<div className={styles.stepItem}>
								<div style={{background: currentStep >= step.id ? "#" + org.background_color : "", borderColor: currentStep >= step.id ? "#" + org.background_color : "", '--step-color-light': `#${org.background_color}33`, '--step-color-lighter': `#${org.background_color}1a`}} className={`${styles.stepCircle} ${currentStep >= step.id ? styles.stepCircleActive : ''} ${currentStep === step.id ? styles.stepCircleCurrent : ''}`}></div>
								<div style={{color: currentStep === step.id ? "#" + org.background_color : ""}} className={`${styles.stepLabel} ${currentStep === step.id ? styles.stepLabelActive : ''}`}>
									{step.label}
								</div>
							</div>
							{index < steps.length - 1 && (
								<div style={{backgroundColor: currentStep > step.id ? "#" + org.background_color : ""}} className={`${styles.stepLine} ${currentStep > step.id ? styles.stepLineActive : ''}`}></div>
							)}
						</div>
					))}
				</div>

				{!hasJoinedQueue ? (
					<div className={styles.cVPeND}>
						{loadingUser ? (
							<div className={styles.hTRmfd}>
                                <Loading />
							</div>
						) : !user ? (
							<>
								<div className={styles.waitingRoomMessage}>
									<p style={{fontSize:'15px',color:'#333'}}>You must be signed in to join the waiting room.</p>
								</div>
								<a
									href={handleAuthRedirect()}
									className={styles.joinQueueBtn}
									style={{background: `linear-gradient(135deg, #${org.background_color} 0%, #${org.background_color} 100%)`, textDecoration: 'none', display: 'inline-block', textAlign: 'center'}}
								>
									Sign In to Continue
								</a>
							</>
						) : (
							<>
								<div className={styles.waitingRoomMessage}>
									<p style={{fontSize:'15px',color:'#333'}}>Complete the verification below to join the waiting room.</p>
								</div>

								<div className={styles.recaptchaContainer}>
									<div
										className="g-recaptcha"
										data-sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
										data-callback="onRecaptchaVerify"
										data-expired-callback="onRecaptchaExpired"
									></div>
								</div>

								{error && (
									<div className={styles.waitingRoomMessage} style={{background:'#fff3cd',borderColor:'#ffc107',marginTop:'12px'}}>
										<p style={{color:'#856404'}}>{error}</p>
									</div>
								)}

								<button
									className={styles.joinQueueBtn}
									style={{background: recaptchaVerified ? `linear-gradient(135deg, #${org.background_color} 0%, #${org.background_color} 100%)` : '#ccc'}}
									onClick={joinQueue}
									disabled={!recaptchaVerified || loading}
								>
									{loading ? 'Joining...' : 'Join Waiting Room'}
								</button>
							</>
						)}
					</div>
				) : loading ? (
					<div className={styles.cVPeND}>
						<div className={styles.hTRmfd}>
							<img src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/login-loading.svg" alt="Loading" title="Loading" width="100" />
						</div>
						<p className={styles.yyTBxq}>Joining the waiting room...</p>
					</div>
				) : queueStatus === 'CHECKOUT' ? (
					<div className={styles.cVPeND}>
						<div className={styles.hTRmfd}>
							<img src="https://d2jun3ty2bmcrf.cloudfront.net/website/img/login-loading.svg" alt="Loading" title="Loading" width="100" />
						</div>
						<p className={styles.yyTBxq}>You've been granted access! Taking you to ticket selection...</p>
					</div>
				) : (queueStatus === 'WAITING' || queueStatus === 'PRE_QUEUE') ? (
					<div className={styles.cVPeND}>
						<div className={styles.waitingRoomInfo}>
							<div className={styles.timeRemainingBox}  style={{flex:'1 1 100%', background: `linear-gradient(135deg, #${org.background_color} 0%, #${org.background_color} 100%)`}}>
								<div className={styles.queueLabel}>Sale Starts In</div>
								<div className={styles.queueTime}>{formatTime(secondsRemaining)}</div>
							</div>
						</div>

						<div className={styles.waitingRoomMessage}>
							<p><strong>You're in the waiting room for {event.title}</strong></p>
							<p>Sale starts: {event.formatted_countdown_booking_start_date}</p>
							<p style={{marginTop:'8px',fontSize:'13px',color:'#666'}}>When tickets go on sale, everyone will be randomly assigned a queue position.</p>
						</div>
						<CheckLoginCheck event_id={event.event_id} org_name={org.name} />
					</div>
				) : queueStatus === 'QUEUED' ? (
					<div className={styles.cVPeND}>
                        <p className={styles.queuePositionText} style={{color: "#" + org.background_color}}>{queuePosition + 1}</p>
                        <p className={styles.queuePositionHeader}>{queuePosition === 0 ? "PERSON AHEAD OF YOU" : "PEOPLE AHEAD OF YOU"}</p>
						<div className={styles.queueProgressContainer}>
							<div className={styles.queueProgressBar}>
								<div
									className={styles.queueProgressFill}
									style={{background: `linear-gradient(90deg, #${org.background_color} 0%, #${org.background_color} 100%)`, width: `${((Math.max(initialQueuePosition, 50) - queuePosition) / Math.max(initialQueuePosition, 50)) * 100}%`}}
								>
									<div style={{borderColor: "#" + org.background_color}} className={styles.queueProgressMarker}>
										{org.logo_image ? (
											<img src={`https://d2jun3ty2bmcrf.cloudfront.net/org/${org.url_string}/${org.logo_image}`} alt={org.name} />
										) : null}
									</div>
								</div>
							</div>
							<p className={styles.queueProgressTime} style={{marginBottom: "0px"}}>ESTIMATED WAIT TIME</p>
                            <p className={styles.queueProgressTime} style={{marginTop: "1px"}}>{typeof queuePosition === 'number' ? Math.ceil(queuePosition / 10) : '--'} min</p>
						</div>

						<div className={styles.waitingRoomMessage}>
							<p><strong>You're in the queue for {event.title}</strong></p>
							<p>Users are being let in batches. You'll be redirected automatically when it's your turn.</p>
							{statusCheckCountdown !== null && statusCheckCountdown > 0 && (
								<p className={styles.statusCheckCountdown}>The queue will be updated in {statusCheckCountdown}s</p>
							)}
						</div>

						<CheckLoginCheck event_id={event.event_id} org_name={org.name} />
					</div>
				) : error ? (
					<div className={styles.cVPeND}>
						<div className={styles.waitingRoomMessage} style={{background:'#fff3cd',borderColor:'#ffc107'}}>
							<p><strong>Error</strong></p>
							<p>{error}</p>
						</div>
						<button className={styles.wREcvg} onClick={() => window.location.reload()} style={{marginTop:'16px'}}>
							Reload Page
						</button>
					</div>
				) : null}
			</div>
		</div>
		{!loading && queueStatus !== 'CHECKOUT' && !error && (
			<div className={styles.helperContainer}>
				<div className={styles.arTvz7}>
					<h3 className={styles.iYQXGs}>Stick to One Device</h3>
					<div className={styles.mKLoVf}><span className={styles.xBnUtQ} style={{borderColor: "#" + org.background_color}}></span></div>
					<p className={styles.yyTBxq}>Stay on the same device throughout the process. Switching devices will reset your queue position.</p>
				</div>
				<div className={styles.arTvz7}>
					<h3 className={styles.iYQXGs}>One Tab Only</h3>
					<div className={styles.mKLoVf}><span className={styles.xBnUtQ} style={{borderColor: "#" + org.background_color}}></span></div>
					<p className={styles.yyTBxq}>Keep only one tab open. Opening multiple tabs will not improve your chances and may cause issues.</p>
				</div>
				<div className={styles.arTvz7}>
					<h3 className={styles.iYQXGs}>Bot Detection is Live</h3>
					<div className={styles.mKLoVf}><span className={styles.xBnUtQ} style={{borderColor: "#" + org.background_color}}></span></div>
					<p className={styles.yyTBxq}>Our system actively monitors for automated behaviour. Suspicious activity will result in removal from the queue.</p>
				</div>
			</div>
		)}
        </div>
	)
}
